/* LEGEND — floating PULLBACK (PBL-v12) signal card. SIGNAL-ONLY: renders the
   would-be commands the worker's PullbackPilot publishes on `pbl:{SYM}`
   (architecture/pbl-v12.mermaid) — nothing here is executed. Draggable /
   collapsible, modeled on the Supply card. Data arrives as a prop (ChartWindow owns
   the getPbl hydrate + subscribePbl live feed). */
import React from 'react';
import { Icon } from './Widgets';

const { useRef: useRefP } = React;
const h = React.createElement;

const ACTION_META: Record<string, { label: string; color: string }> = {
  BUY_DIP: { label: 'BUY DIP', color: 'var(--up)' },
  BUY_NOW: { label: 'BUY NOW', color: 'var(--up)' },
  BUY_ON_BREAK: { label: 'BUY BREAK', color: 'var(--up)' },
  SCALE_IN: { label: 'SCALE IN', color: 'var(--t1, #3a6fd8)' },
  SET_TARGET_PRESET: { label: 'TARGETS', color: 'var(--t1, #3a6fd8)' },
  SELL_PERCENT: { label: 'TRIM', color: 'var(--warn)' },
  MOVE_STOP: { label: 'STOP → BE', color: 'var(--warn)' },
  CLOSE_NOW: { label: 'CLOSE', color: 'var(--down)' },
};

function actionMeta(a: string) {
  return ACTION_META[a] || { label: String(a || '—'), color: 'var(--fg, #ccc)' };
}
function gradeColor(g: string) {
  return g === 'A' ? 'var(--up)' : g === 'B' ? 'var(--warn)' : 'var(--muted, #8a8a8a)';
}
function etLabel(m: number) {
  if (!(m >= 0)) return '';
  const hh = Math.floor(m / 60);
  const mm = m % 60;
  return hh + ':' + String(mm).padStart(2, '0');
}
function fmtNum(n: any, p = 2) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toFixed(v !== 0 && Math.abs(v) < 1 ? 4 : p);
}

function badge(text: string, color: string, extra?: any) {
  return h(
    'span',
    {
      style: {
        display: 'inline-block',
        padding: '1px 7px',
        borderRadius: 4,
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '.04em',
        color,
        border: '1px solid ' + color,
        ...(extra || {}),
      },
    },
    text,
  );
}

function hero(sig: any) {
  const am = actionMeta(sig.action);
  const rrTxt = Number(sig.rr) > 0 ? 'R:R ' + fmtNum(sig.rr, 1) : '';
  const isEntry = sig.action === 'BUY_DIP' || sig.action === 'BUY_NOW' || sig.action === 'BUY_ON_BREAK';
  return h(
    'div',
    { style: { padding: '10px 12px', borderBottom: '1px solid var(--line, #23262e)' } },
    h(
      'div',
      { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 } },
      badge(am.label, am.color, { fontSize: 12, padding: '2px 9px' }),
      sig.entryType && sig.entryType !== 'manage'
        ? h('span', { style: { fontSize: 11, color: 'var(--muted, #8a8a8a)' } }, String(sig.entryType))
        : null,
      h('span', { style: { flex: 1 } }),
      badge('Grade ' + (sig.grade || '—'), gradeColor(sig.grade)),
      h(
        'span',
        { style: { fontSize: 11, color: 'var(--muted, #8a8a8a)' } },
        etLabel(Number(sig.etMinute)),
      ),
    ),
    h(
      'div',
      { style: { display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 } },
      h('span', { className: 'num', style: { fontSize: 20, fontWeight: 700 } }, fmtNum(sig.px)),
      isEntry
        ? h(
            'span',
            { className: 'num', style: { fontSize: 11.5, color: 'var(--muted, #8a8a8a)' } },
            'stop ' + fmtNum(sig.stopRef) + (rrTxt ? '  ·  ' + rrTxt : ''),
          )
        : sig.action === 'SELL_PERCENT'
          ? h('span', { style: { fontSize: 12, color: 'var(--warn)' } }, 'trim ' + fmtNum(sig.percent, 0) + '%')
          : sig.action === 'SET_TARGET_PRESET'
            ? h('span', { style: { fontSize: 12, color: 'var(--t1, #3a6fd8)' } }, String(sig.preset || ''))
            : null,
    ),
    Number(sig.level) > 0
      ? h(
          'div',
          { style: { fontSize: 11, color: 'var(--muted, #8a8a8a)', marginBottom: 4 } },
          'defended ' + fmtNum(sig.level) + (Number(sig.levelDefenseCount) > 0 ? '  ·  ' + sig.levelDefenseCount + '× def' : ''),
        )
      : null,
    sig.reason ? h('div', { style: { fontSize: 11.5, color: 'var(--fg, #c8ccd4)', lineHeight: 1.35 } }, String(sig.reason)) : null,
  );
}

function row(sig: any, i: number) {
  const am = actionMeta(sig.action);
  return h(
    'div',
    {
      key: 'r' + i + '_' + (sig.ts || ''),
      style: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '5px 12px',
        borderBottom: '1px solid var(--line, #1c1f26)',
        fontSize: 11,
      },
    },
    h('span', { style: { minWidth: 62, color: am.color, fontWeight: 700 } }, am.label),
    h('span', { className: 'num', style: { minWidth: 54 } }, fmtNum(sig.px)),
    h('span', { style: { color: gradeColor(sig.grade), fontWeight: 700 } }, sig.grade || ''),
    h('span', { style: { flex: 1 } }),
    h('span', { style: { color: 'var(--muted, #8a8a8a)' } }, etLabel(Number(sig.etMinute))),
  );
}

export function PblCard(props: any) {
  const { report: r, symbol, pos, collapsed, onMove, onToggle, onClose, docked } = props;
  const cardRef = useRefP<any>(null);

  function startDrag(e: any) {
    if (e.target.closest('button') || e.target.closest('a')) return;
    e.preventDefault();
    const ox = pos.x == null && cardRef.current ? cardRef.current.offsetLeft : pos.x || 0;
    const sx = e.clientX,
      sy = e.clientY,
      oy = pos.y;
    const move = (ev: any) => onMove({ x: ox + (ev.clientX - sx), y: oy + (ev.clientY - sy) });
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  const signals: any[] = (r && r.signals) || [];
  const latest = signals[0] || null;

  const head = h(
    'div',
    { className: 'dil-head', onPointerDown: docked ? undefined : startDrag },
    h('span', { className: 'pc-sym' }, symbol),
    h('span', { className: 'dil-kicker' }, 'Pullback'),
    latest ? badge(actionMeta(latest.action).label, actionMeta(latest.action).color) : null,
    h(
      'div',
      { className: 'pc-actions' },
      h('button', { className: 'icon-btn sm', onClick: onToggle, title: collapsed ? 'Expand' : 'Collapse' }, Icon.chev()),
      h('button', { className: 'icon-btn sm', onClick: onClose, title: 'Close card' }, Icon.x()),
    ),
  );

  const body = latest
    ? h(
        'div',
        { className: 'dil-body' },
        hero(latest),
        signals.length > 1
          ? h(
              'div',
              null,
              h(
                'div',
                { style: { fontSize: 10, letterSpacing: '.08em', color: 'var(--muted, #8a8a8a)', padding: '6px 12px 2px' } },
                'RECENT',
              ),
              signals.slice(1, 9).map((s, i) => row(s, i)),
            )
          : null,
      )
    : h(
        'div',
        { className: 'dil-body', style: { padding: 16, color: 'var(--muted, #8a8a8a)', fontSize: 12 } },
        'No pullback signals yet today.',
      );

  const cardStyle = docked
    ? undefined
    : pos.x == null
      ? { right: '14px', top: pos.y + 'px' }
      : { left: pos.x + 'px', top: pos.y + 'px' };

  return h(
    'div',
    { className: 'dil-card' + (collapsed ? ' collapsed' : '') + (docked ? ' docked' : ''), style: cardStyle, ref: cardRef },
    head,
    collapsed ? null : body,
  );
}
